import { all, call, put, select, takeLatest } from "redux-saga/effects";
import queryString from "query-string";
import { toast } from "react-toastify";
import { normalize, schema } from "normalizr";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { ITEMS_PER_PAGE } from "constants/general";
import {
    attachExistedTag,
    attachNewTag,
    createProxies,
    createProxy,
    deleteProxy,
    detachTag,
    getCountProxies,
    getCountProxiesError,
    getCountProxiesSuccess,
    getProxies,
    getProxiesError,
    getProxiesSuccess,
    mergeAttachExistedTagDialog,
    mergeAttachNewTagDialog,
    mergeDetachTagDialog,
    mergeImportProxiesDialog, mergeProxyDialog,
    setAttachExistedTagDialog,
    setAttachNewTagDialog,
    setDetachTagDialog,
    setImportProxiesDialog, setProxyDialog
} from "modules/ProxyManager/services/Proxies/actions";
import {
    getCountProxiesRequest,
    getProxiesRequest,
    handleAttachTagRequest,
    handleCreateProxyRequest,
    handleDeleteProxyRequest,
    handleDetachTagRequest
} from "modules/ProxyManager/services/Proxies/api";
import { attachExistedTagDialogSelector,
    attachNewTagDialogSelector,
    detachTagDialogSelector
} from "modules/ProxyManager/services/Proxies/selectors";

const proxiesSchema = new schema.Entity("proxies");
const proxiesListSchema = new schema.Array(proxiesSchema);

//Workers
function* handleRefreshProxies() {
    const { search } = window.location;
    yield put(getProxies({ limit: ITEMS_PER_PAGE, ...queryString.parse(search) }));
}

function* handleGetProxies({ payload }) {
    try {
        const { proxies } = yield call(getProxiesRequest, payload);
        const { result, entities } = normalize(proxies, proxiesListSchema);
        yield put(getProxiesSuccess({
            proxies: entities.proxies,
            ids: result
        }));

    } catch (error) {
        yield put(getProxiesError());
        getErrorMessage(error);
    }
}

function* handleDeleteProxy({ payload }) {
    try {
        let data = [];
        if (payload) {
            data = Array.isArray(payload) ? payload : [ payload ];
        }
        yield all(data.map((id) => call(handleDeleteProxyRequest, id)));
        yield call(
            toast.success,
            `The ${data.length > 1 ? "Proxies" : "Proxy"} with ID${data.length > 1 ? "s" : ""} - 
            ${data.join(", ")} ${data.length > 1 ? "have" : "has"} been successfully deleted`
        );
        yield handleRefreshProxies();
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleAttachExistedTag({ payload }) {
    try {
        yield put(mergeAttachExistedTagDialog({ isLoading: true }));
        const { selected } = yield select(attachExistedTagDialogSelector);
        let expiredAtTimestamp = 0;
        if (payload.expired_at_timestamp) {
            expiredAtTimestamp = Math.floor(new Date(payload.expired_at_timestamp).getTime() / 1000);
        }
        let requests = [];
        selected.forEach((id) => {
            payload.selected_tags.forEach((tag) => {
                requests.push({
                    id,
                    body: {
                        name: tag,
                        expired_at_timestamp: expiredAtTimestamp
                    }
                });
            });
        });
        yield all(
            requests.map((requestBody) => call(handleAttachTagRequest, requestBody))
        );
        yield put(setAttachExistedTagDialog());
        yield handleRefreshProxies();
    } catch (error) {
        yield put(mergeAttachExistedTagDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleAttachNewTag({ payload }) {
    try {
        yield put(mergeAttachNewTagDialog({ isLoading: true }));
        const { selected } = yield select(attachNewTagDialogSelector);
        let expiredAtTimestamp = 0;
        if (payload.expired_at_timestamp) {
            expiredAtTimestamp = Math.floor(new Date(payload.expired_at_timestamp).getTime() / 1000);
        }
        yield all(
            selected.map((id) => call(handleAttachTagRequest, {
                id,
                body: {
                    name: payload.tag,
                    expired_at_timestamp: expiredAtTimestamp
                }
            }))
        );
        yield put(setAttachNewTagDialog());
        yield handleRefreshProxies();
    } catch (error) {
        yield put(mergeAttachNewTagDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleDetachTag({ payload }) {
    try {
        yield put(mergeDetachTagDialog({ isLoading: true }));
        const { selected } = yield select(detachTagDialogSelector);
        let requests = [];
        selected.forEach((id) => {
            payload.selected_tags.forEach((tag) => {
                requests.push({ id, tag });
            });
        });
        yield all(
            requests.map((requestBody) => call(handleDetachTagRequest, requestBody))
        );
        yield put(setDetachTagDialog());
        yield handleRefreshProxies();
    } catch (error) {
        yield put(mergeDetachTagDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleCreateProxies({ payload }) {
    const requestBody = { proxies: [] };

    if (payload.length === 0) {
        getErrorMessage("CSV file is not selected or invalid");
        return;
    }

    try {
        payload.forEach((row) => {
            let tags = [];
            if (row.Tags) {
                let tagNames = row.Tags.split("|");
                for (let k in tagNames) {
                    if (tagNames[k] && tagNames[k].trim().length > 0) {
                        tags.push({ name: tagNames[k].trim() });
                    }
                }
            }

            requestBody.proxies.push({
                ip: row.IP,
                port: parseInt(row["HTTP Port"]),
                socks5_port: parseInt(row["SOCKS5 Port"]),
                username: row.Username,
                password: row.Password,
                tags: tags
            });
        });

        yield put(mergeImportProxiesDialog({ isLoading: true }));
        yield call(handleCreateProxyRequest, requestBody);
        yield put(setImportProxiesDialog());
        yield handleRefreshProxies();
    } catch (error) {
        yield put(mergeAttachNewTagDialog({ isLoading: false }));

        getErrorMessage(error);
    }
}

function* handleCreateProxy({ payload }) {
    const requestBody = { proxies: [] };

    try {
        let tags = [];
        if (payload.tags) {
            for (let k in payload.tags) {
                tags.push({ name: payload.tags[k] });
            }
        }
        requestBody.proxies.push({
            ip: payload.ip,
            port: parseInt(payload.port),
            socks5_port: payload.socks5_port ? parseInt(payload.socks5_port) : 0,
            username: payload.username,
            password: payload.password,
            tags: tags
        });
        yield call(handleCreateProxyRequest, requestBody);
        yield put(setProxyDialog());
        yield call(toast.success, "The proxy has been successfully created");
        yield put(mergeProxyDialog({ isLoading: true }));
        yield handleRefreshProxies();
    } catch (error) {
        yield put(mergeProxyDialog({ isLoading: false }));

        getErrorMessage(error);
    }
}

function* handleGetCountProxies({ payload }) {
    try {
        const { count } = yield call(getCountProxiesRequest, payload);
        yield put(getCountProxiesSuccess(count));
    } catch (error) {
        yield put(getCountProxiesError());

        getErrorMessage(error);
    }
}

// Watchers
export function* proxiesSagas() {
    yield all([
        yield takeLatest(getProxies, handleGetProxies),
        yield takeLatest(deleteProxy, handleDeleteProxy),
        yield takeLatest(attachExistedTag, handleAttachExistedTag),
        yield takeLatest(attachNewTag, handleAttachNewTag),
        yield takeLatest(detachTag, handleDetachTag),
        yield takeLatest(createProxies, handleCreateProxies),
        yield takeLatest(getCountProxies, handleGetCountProxies),
        yield takeLatest(createProxy, handleCreateProxy),
    ]);
}
